// Diagnostica tecnica della connessione di una chiamata (TODO #9).
//
// Ogni lato (chiamante e consulente) campiona RTCPeerConnection.getStats()
// poco dopo il 'connected' (o al 'failed') e lo invia al server con
// `call:diagnostics`, insieme a ping del socket di segnalazione, info rete
// (navigator.connection) e browser/OS/dispositivo. Tutto best-effort: nessuna
// eccezione deve mai risalire alla chiamata.
import type { Socket } from 'socket.io-client';
import type {
  CallNetCandidateType,
  CallNetDiagnostics,
  CallNetDiagPhase,
  CallNetProtocol,
} from '@klaro7/shared-types';

import { parseDeviceInfo } from './deviceInfo';

type StatsEntry = Record<string, unknown> & { id: string; type: string };

/** Minimo di RTCStatsReport che serve al parser (anche una Map finta nei test). */
export interface StatsReportLike {
  forEach(cb: (value: unknown) => void): void;
}

const CANDIDATE_TYPES = new Set(['host', 'srflx', 'prflx', 'relay']);

const numOrUndef = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

const round = (v: number, decimals = 1) => {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
};

/**
 * Estrae le metriche dal report di getStats(): candidate pair selezionata
 * (RTT, banda in uscita, tipi di candidato → relay TURN, protocollo) e
 * inbound-rtp audio (jitter, pacchetti persi/ricevuti, codec). Pura.
 */
export function parseRtcStats(report: StatsReportLike): CallNetDiagnostics {
  const byId = new Map<string, StatsEntry>();
  report.forEach((raw) => {
    const s = raw as StatsEntry;
    if (s && typeof s.id === 'string') byId.set(s.id, s);
  });
  const all = [...byId.values()];
  const out: CallNetDiagnostics = {};

  // Candidate pair selezionata: via transport (Chromium/Safari), altrimenti
  // flag `selected` (Firefox) o nominated+succeeded.
  const transport = all.find((s) => s.type === 'transport' && s.selectedCandidatePairId);
  let pair = transport ? byId.get(String(transport.selectedCandidatePairId)) : undefined;
  if (!pair) {
    const pairs = all.filter((s) => s.type === 'candidate-pair');
    pair =
      pairs.find((p) => p.selected === true) ??
      pairs.find((p) => p.nominated === true && p.state === 'succeeded');
  }

  if (pair) {
    const rtt = numOrUndef(pair.currentRoundTripTime);
    if (rtt !== undefined) out.rttMs = round(rtt * 1000);
    const bitrate = numOrUndef(pair.availableOutgoingBitrate);
    if (bitrate !== undefined) out.outgoingBitrateKbps = round(bitrate / 1000);

    const local = byId.get(String(pair.localCandidateId));
    const remote = byId.get(String(pair.remoteCandidateId));
    const lt = local?.candidateType;
    const rt = remote?.candidateType;
    if (typeof lt === 'string' && CANDIDATE_TYPES.has(lt)) out.localCandidateType = lt as CallNetCandidateType;
    if (typeof rt === 'string' && CANDIDATE_TYPES.has(rt)) out.remoteCandidateType = rt as CallNetCandidateType;
    if (out.localCandidateType || out.remoteCandidateType) {
      out.relay = out.localCandidateType === 'relay' || out.remoteCandidateType === 'relay';
    }
    const proto = local?.protocol;
    if (proto === 'udp' || proto === 'tcp') out.protocol = proto as CallNetProtocol;
  }

  const inbound = all.find(
    (s) => s.type === 'inbound-rtp' && (s.kind === 'audio' || s.mediaType === 'audio')
  );
  if (inbound) {
    const jitter = numOrUndef(inbound.jitter);
    if (jitter !== undefined) out.jitterMs = round(jitter * 1000);
    const lost = numOrUndef(inbound.packetsLost);
    const received = numOrUndef(inbound.packetsReceived);
    if (lost !== undefined) out.packetsLost = Math.max(0, lost);
    if (received !== undefined) out.packetsReceived = received;
    if (lost !== undefined && received !== undefined) {
      const total = Math.max(0, lost) + received;
      out.packetLossPct = total > 0 ? round((Math.max(0, lost) / total) * 100, 2) : 0;
    }
    const codec = inbound.codecId ? byId.get(String(inbound.codecId)) : undefined;
    if (typeof codec?.mimeType === 'string') out.audioCodec = codec.mimeType;
  }

  // RTT dal report RTCP del peer quando la pair non lo espone (alcuni Safari).
  if (out.rttMs === undefined) {
    const remoteInbound = all.find(
      (s) => s.type === 'remote-inbound-rtp' && (s.kind === 'audio' || s.mediaType === 'audio')
    );
    const rtt = numOrUndef(remoteInbound?.roundTripTime);
    if (rtt !== undefined) out.rttMs = round(rtt * 1000);
  }

  return out;
}

/** Nome + versione principale del browser dal User-Agent. Puro. */
export function parseBrowser(ua: string | undefined): string | undefined {
  if (!ua) return undefined;
  const rules: Array<[RegExp, string]> = [
    [/EdgA?\/(\d+)/, 'Edge'],
    [/SamsungBrowser\/(\d+)/, 'Samsung Internet'],
    [/OPR\/(\d+)/, 'Opera'],
    [/FxiOS\/(\d+)/, 'Firefox'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/CriOS\/(\d+)/, 'Chrome'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+)[\d.]*.*Safari\//, 'Safari'],
  ];
  for (const [re, name] of rules) {
    const m = ua.match(re);
    if (m) return `${name} ${m[1]}`;
  }
  if (/Safari\//.test(ua) && /iPhone|iPad|Macintosh/.test(ua)) return 'Safari';
  return undefined;
}

interface NetworkInformationLike {
  type?: string;
  effectiveType?: string;
  downlink?: number;
}

/** Rete + browser/OS/dispositivo del client corrente. */
export function collectEnvironmentInfo(deviceModel?: string): CallNetDiagnostics {
  const out: CallNetDiagnostics = {};
  try {
    const conn = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    if (conn?.type) out.networkType = conn.type;
    if (conn?.effectiveType) out.effectiveType = conn.effectiveType;
    const downlink = numOrUndef(conn?.downlink);
    if (downlink !== undefined) out.downlinkMbps = downlink;
    const ua = navigator.userAgent;
    out.browser = parseBrowser(ua);
    const info = parseDeviceInfo(ua, deviceModel);
    if (info) {
      out.os = info.label.split(' · ')[0];
      if (info.model) out.deviceModel = info.model;
    }
  } catch {
    // best-effort
  }
  return out;
}

/**
 * Ping applicativo sul socket di segnalazione (evento `call:ping` con ack):
 * mediana di `count` round-trip. Funziona prima che la media sia su.
 * undefined se il server non risponde entro il timeout.
 */
export async function measureSocketPing(
  socket: Pick<Socket, 'emit' | 'connected'>,
  count = 3,
  timeoutMs = 2000
): Promise<number | undefined> {
  const samples: number[] = [];
  for (let i = 0; i < count; i++) {
    if (!socket.connected) break;
    const rtt = await new Promise<number | undefined>((resolve) => {
      const t0 = performance.now();
      const timer = setTimeout(() => resolve(undefined), timeoutMs);
      try {
        socket.emit('call:ping', {}, () => {
          clearTimeout(timer);
          resolve(performance.now() - t0);
        });
      } catch {
        clearTimeout(timer);
        resolve(undefined);
      }
    });
    if (rtt !== undefined) samples.push(rtt);
  }
  if (samples.length === 0) return undefined;
  samples.sort((a, b) => a - b);
  return round(samples[Math.floor(samples.length / 2)] ?? 0);
}

/**
 * Campione completo: getStats() (avviato in modo sincrono, così funziona anche
 * se subito dopo la pc viene chiusa) + ambiente + ping. Non lancia mai.
 */
export async function collectNetDiagnostics(
  pc: RTCPeerConnection,
  phase: CallNetDiagPhase,
  extras: {
    pingMs?: number | Promise<number | undefined>;
    deviceModel?: string | Promise<string | undefined>;
  } = {}
): Promise<CallNetDiagnostics> {
  let statsPromise: Promise<RTCStatsReport | null>;
  try {
    statsPromise = pc.getStats().catch(() => null);
  } catch {
    statsPromise = Promise.resolve(null);
  }
  let stats: CallNetDiagnostics = {};
  try {
    const report = await statsPromise;
    if (report) stats = parseRtcStats(report);
  } catch {
    // best-effort
  }
  let pingMs: number | undefined;
  let deviceModel: string | undefined;
  try {
    [pingMs, deviceModel] = await Promise.all([extras.pingMs, extras.deviceModel]);
  } catch {
    // best-effort
  }
  return {
    phase,
    ...(pingMs !== undefined ? { pingMs } : {}),
    ...stats,
    ...collectEnvironmentInfo(deviceModel),
  };
}

export type NetQuality = 'good' | 'fair' | 'poor';

/**
 * Giudizio sintetico per l'admin. Soglie: Buona = RTT < 150 ms, perdita < 1%,
 * jitter < 30 ms; Discreta = RTT < 300 ms, perdita < 5% (jitter < 60 ms);
 * altrimenti Scarsa. Un fallimento ICE è sempre Scarsa. Le metriche mancanti
 * non penalizzano; null se non c'è alcuna metrica.
 */
export function netQuality(d: CallNetDiagnostics | null | undefined): NetQuality | null {
  if (!d) return null;
  if (d.phase === 'failed') return 'poor';
  const rtt = d.rttMs ?? d.pingMs;
  const loss = d.packetLossPct;
  const jitter = d.jitterMs;
  if (rtt === undefined && loss === undefined && jitter === undefined) return null;
  const within = (v: number | undefined, max: number) => v === undefined || v < max;
  if (within(rtt, 150) && within(loss, 1) && within(jitter, 30)) return 'good';
  if (within(rtt, 300) && within(loss, 5) && within(jitter, 60)) return 'fair';
  return 'poor';
}
